import axios from 'axios';
import { FormEvent, useEffect, useState } from 'react';
import { Modal as ModalContainer } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { AiOutlineClose } from 'react-icons/ai';
import { FaBuilding } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useCompany } from '../../../hooks/useCompany';
import { api } from '../../../services/axios';
import Loader from '../../Shared/Loader';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup';
import './styles.css';
import { useForm } from 'react-hook-form';

interface CompanyProps {
    id: string;
    region: string;
    uf: string;
    cep: string;
    address: string;
    sector: string;
    description: string;
    city: string;
    photoUrl: string;
}

interface CompanyHeaderModalProps {
    toggle: () => void;
    isShowing: boolean;
}

interface Address {
    bairro: string;
    cep: string;
    complemento: string;
    ddd: string;
    gia: string;
    ibge: string;
    localidade: string;
    logradouro: string;
    siafi: string;
    uf: string;
}

interface UpdateCompanyHeaderProps {
    id: string;
    serviceId: string;
    region: string;
    uf: string;
    cep: string;
    address: string;
    city: string;
    sector: string;
    description: string;
}

const schema = yup.object().shape({
    cep: yup
        .string()
        .required('O Campo CEP é obrigatório')
        .trim()
        .max(8, 'Por favor, insira um valor válido para o campo CEP')
        .min(8, 'Por favor, insira um valor válido para o campo CEP'),

    city: yup
        .string()
        .required('O Campo Município é obrigatório')
        .trim(),

    address: yup
        .string()
        .required('O Campo Endereço é obrigatório')
        .trim(),

    uf: yup
        .string()
        .required('O Campo UF é obrigatório')
        .trim(),

    region: yup
        .string()
        .required('O Campo Região é obrigatório')
        .trim(),

    description: yup
        .string()
        .required('O Campo Descrição é obrigatório')
        .trim()
        .max(255, 'Por favor, insira um valor entre 5 e 255 caracteres')
        .min(5, 'Por favor, insira um valor entre 5 e 255 caracteres'),

    sector: yup
        .string()
        .required('O Campo Setor é obrigatório')
        .trim()

});

export function CompanyHeaderModal({ toggle, isShowing }: CompanyHeaderModalProps) {
    const [loading, setLoading] = useState(false);
    const [companyData, setCompanyData] = useState<CompanyProps>();
    const [cep, setCep] = useState('');
    const [showDropzone, setShowDropzone] = useState(true);
    const [filePreview, setFilePreview] = useState("");
    const { company, changeCompanyPhoto } = useCompany();
    const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'image/gif': ['.gif'],
            'image/jpeg': ['.jpeg'],
            'image/jpg': ['.jpg'],
            'image/svg': ['.svg'],
        },
        maxFiles: 1,
        onDrop: files => onDropImage(files)
    });
    const [address, setAddress] = useState<Address>({
        bairro: '',
        cep: '',
        complemento: '',
        ddd: '',
        gia: '',
        ibge: '',
        localidade: '',
        logradouro: '',
        siafi: '',
        uf: ''
    });

    const { register, handleSubmit, control, formState: { errors }, setValue } = useForm<UpdateCompanyHeaderProps>({
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        async function getCompany() {
            setLoading(true);
            try {
                const result = await api.get(`company/Get/${company.id}`);
                const response = await result.data;


                if (response.statusCode === 200) {
                    const data: CompanyProps = response.data;

                    setValue('address', data.address);
                    setValue('cep', data.cep);
                    setValue('city', data.city);
                    setValue('region', data.region);
                    setValue('uf', data.uf);
                    setValue('sector', data.sector);
                    setValue('description', data.description);

                    if (data.photoUrl) {
                        setFilePreview(data.photoUrl);
                        setShowDropzone(false);
                    }
                    else{
                        setShowDropzone(true);
                    }

                    setCompanyData(data);
                }
            }
            catch (err: any) {

            }
            finally {
                setLoading(false);
            }
        }

        getCompany();
    }, []);

    async function getDataByCEP() {
        setLoading(true);
        try {
            const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
            if (response.data.hasOwnProperty('erro')) {
                if (response.data.erro === true) {
                    toast.error('CEP não encontrado!');
                }
            }
            if (response.status === 200) {
                const data: Address = await response.data;
                setValue('address', data.logradouro);
                setValue('city', data.localidade);
                setValue('region', data.bairro);
                setValue('uf', data.uf);
                setAddress(data);

            }
        }
        catch (err: any) {
            toast.error('Erro ao buscar o endereço solicitado, por favor verifique se o CEP está correto!');
            setValue('address', '');
            setValue('city', '');
            setValue('region', '');
            setValue('uf', '');
        }
        finally {
            setLoading(false);
        }

    }

    async function saveChanges(data: UpdateCompanyHeaderProps) {
        data.id = company.id;
        data.serviceId = company.serviceId;
        setLoading(true);

        if (filePreview == "") {
            toast.error('Por favor, insira uma imagem para continuar!');
            setLoading(false);
            return;
        }

        try {
            const result = await api.put(`company/put`, data);
            const response = await result.data;

            if (response.statusCode === 200) {
                toast.success('Dados alterados com sucesso!');
                await uploadFile(data.id);
            }
        }
        catch (err: any) {
        }
        finally {
            setLoading(false);
        }
    }

    async function uploadFile(id: string) {
        setLoading(true);
        let blob: Blob = acceptedFiles[0];
        try {
            if (blob != undefined) {
                let formData = new FormData();
                formData.append('id', id);
                formData.append('serviceId', company.serviceId);
                formData.append('file', blob);
                const result = await api.post('company/uploadfile', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                const response = await result.data;
                const data = response.data;
                if (response.statusCode === 200) {
                    changeCompanyPhoto(data.photoUrl);
                    toast.success('Upload da imagem feito com sucesso!');
                }
            }

        }
        catch (err: any) {
            console.log('UPLOAD FILE ERROR', err);
        }
        finally {
            setLoading(false);
        }
    }

    function onDropImage(files: any) {
        let file = files[0]
        if (file && validateFileType(file)) {
            setShowDropzone(false);
            setFilePreview(URL.createObjectURL(file));
        }
        else {
            toast.error('Por favor, insira um documento do tipo imagem, os tipos válidos são : .png, .jpeg, .jpg, .svg e .gif');
        }

    }

    function validateFileType(file: any) {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/gif'];
        return allowedTypes.includes(file.type);
    }

    function handleRemoveFile() {
        setShowDropzone(true);
        setFilePreview("");
    }

    return (
        <>
            {loading && <Loader />}
            <ModalContainer scrollable={true} onHide={toggle} className='modal-lg' show={isShowing}>
                <ModalContainer.Header>
                    <div className='modal-custom-header'>
                        <FaBuilding size={70} />
                        <h3 className='mt-3'>
                            <strong>
                                Editar Empresa
                            </strong>
                        </h3>
                        <p>Para editar a empresa, insira as devidas informações</p>
                    </div>
                </ModalContainer.Header>
                <ModalContainer.Body className='p-4'>
                    <form onSubmit={handleSubmit(saveChanges)}>
                        {showDropzone
                            ?
                            <section className="container mb-4">
                                <div {...getRootProps({ className: 'dropzone' })}>
                                    <input {...getInputProps()} />
                                    <p>Selecione uma imagem para compor a logo da empresa</p>
                                    <p><small>.png, .jpeg, .jpg, .svg, .gif</small></p>
                                </div>
                            </section>
                            :
                            <div className='image-container-preview mb-4 border rounded'>
                                <AiOutlineClose size={25} className='cursor-pointer' onClick={handleRemoveFile} />
                                <img
                                    src={filePreview}
                                    className='img-fluid'
                                />
                            </div>
                        }


                        <div className='row mb-4'>
                            <div className='col-6'>
                                <label className='form-label'>Nome</label>
                                <input type="text" className='form form-control' defaultValue={company.title} disabled />
                            </div>
                            <div className='col-6'>
                                <label className='form-label'>CNPJ</label>
                                <input type="text" className='form form-control' defaultValue={company.taxNumber} disabled />
                            </div>
                        </div>
                        <div className='row mb-4'>
                            <div className='col-6'>
                                <label className='form-label'>Região</label>
                                <input type="text" className='form form-control' {...register('region')} defaultValue={companyData?.region} disabled />
                                <span className='error-field'>{errors?.region?.message}</span>
                            </div>
                            <div className='col-6'>
                                <label className='form-label'>Município</label>
                                <input type="text" className='form form-control' {...register('city')} defaultValue={companyData?.city} disabled />
                                <span className='error-field'>{errors?.city?.message}</span>
                            </div>
                        </div>
                        <div className='row mb-4'>
                            <div className='col-6'>
                                <label className='form-label'>UF</label>
                                <input type="text" className='form form-control' {...register('uf')} defaultValue={companyData?.uf} disabled />
                                <span className='error-field'>{errors?.uf?.message}</span>
                            </div>
                            <div className='col-6'>
                                <label className='form-label'>CEP</label>
                                <input
                                    type="text"
                                    className='form form-control'
                                    defaultValue={companyData?.cep}
                                    {...register('cep', {
                                        onChange: e => setCep(e.target.value),
                                        onBlur: () => getDataByCEP()
                                    })}
                                />
                                <span className='error-field'>{errors?.cep?.message}</span>
                            </div>
                        </div>
                        <div className='row mb-4'>
                            <div className='col-6'>
                                <label className='form-label'>Endereço</label>
                                <input type="text" className='form form-control' {...register('address')} disabled defaultValue={companyData?.address} />
                                <span className='error-field'>{errors?.address?.message}</span>
                            </div>
                            <div className='col-6'>
                                <label className='form-label'>Setor</label>
                                <input type="text" className='form form-control' {...register('sector')} defaultValue={companyData?.sector} />
                                <span className='error-field'>{errors?.sector?.message}</span>
                            </div>
                        </div>
                        <div className='row mb-4'>
                            <div>
                                <label className='form-label'>Descrição</label>
                                <input type="text" className='form-control' {...register('description')} defaultValue={companyData?.description} />
                                <span className='error-field'>{errors?.description?.message}</span>
                            </div>
                        </div>
                        <button className='btn btn-success w-100' type='submit'>
                            Salvar Informações
                        </button>
                    </form>
                </ModalContainer.Body>
            </ModalContainer >
        </>
    );
}